// Data hook for the recipe pending-lines reconcile UI.
//
// Owns: the pending-lines list for the currently-selected recipe, plus the
// match / discard / create-and-match operations. Callers pass `onChanged`
// so they can refresh the recipe detail (matched lines flip from pending
// into the live BOM) and the recipes list (the pending badge updates).

import { useCallback, useEffect, useState } from "react";
import {
  createMaterial,
  listManufacturingRecipePendingLines,
  reconcileManufacturingRecipePendingLine,
} from "@/lib/manufacturingClient";
import type { MfgRecipePendingLine } from "@/lib/manufacturing/types";

export type CreateMaterialFromPendingInput = {
  name: string;
  category: string;
  unit: string;
};

export type ReconcileResult = { ok: true } | { ok: false; error: string };

export function useManufacturingPendingLines(opts: {
  recipeId: string | null;
  onChanged: () => void;
}) {
  const { recipeId, onChanged } = opts;
  const [rows, setRows] = useState<MfgRecipePendingLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!recipeId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    const r = await listManufacturingRecipePendingLines(recipeId);
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "Failed to load pending lines");
      setRows([]);
      return;
    }
    setRows(r.data.pending_lines);
  }, [recipeId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const matchExisting = useCallback(
    async (pendingLineId: string, materialId: string): Promise<ReconcileResult> => {
      setBusyId(pendingLineId);
      const r = await reconcileManufacturingRecipePendingLine({
        pending_line_id: pendingLineId,
        action: "match",
        material_id: materialId,
      });
      setBusyId(null);
      if (!r.ok) return { ok: false, error: r.error || "Reconcile failed" };
      await reload();
      onChanged();
      return { ok: true };
    },
    [onChanged, reload]
  );

  const discard = useCallback(
    async (pendingLineId: string): Promise<ReconcileResult> => {
      setBusyId(pendingLineId);
      const r = await reconcileManufacturingRecipePendingLine({
        pending_line_id: pendingLineId,
        action: "discard",
      });
      setBusyId(null);
      if (!r.ok) return { ok: false, error: r.error || "Discard failed" };
      await reload();
      onChanged();
      return { ok: true };
    },
    [onChanged, reload]
  );

  // Create a new material and immediately match the pending line to it. Two
  // round-trips by design — the new material lands in the catalog
  // independently, so even if the second call fails the operator hasn't
  // lost their work.
  const createAndMatch = useCallback(
    async (
      pendingLineId: string,
      input: CreateMaterialFromPendingInput
    ): Promise<ReconcileResult> => {
      setBusyId(pendingLineId);
      const created = await createMaterial({
        name: input.name,
        category: input.category,
        unit: input.unit,
      });
      if (!created.ok) {
        setBusyId(null);
        return { ok: false, error: created.error || "Material create failed" };
      }
      const newId = created.data.material.id;
      const r = await reconcileManufacturingRecipePendingLine({
        pending_line_id: pendingLineId,
        action: "match",
        material_id: newId,
      });
      setBusyId(null);
      if (!r.ok) return { ok: false, error: r.error || "Reconcile failed" };
      await reload();
      onChanged();
      return { ok: true };
    },
    [onChanged, reload]
  );

  return {
    rows,
    loading,
    error,
    busyId,
    reload,
    matchExisting,
    discard,
    createAndMatch,
  };
}
